import { QueryClient } from '@tanstack/query-core';
import {
  GetMacroplanogramDocument,
  GetMacroplanogramInput,
  GetMacroplanogramResponse,
  GetMacroplanogramQuery,
  GetAssortmentDiffInput,
  GetAssortmentDiffResponse,
  GetAssortmentDiffQuery,
  GetAssortmentDiffDocument,
  GetAssortmentSwapRecommendationsInput,
  GetAssortmentSwapRecommendationsResponse,
  GetAssortmentSwapRecommendationsDocument,
  GetAssortmentSwapRecommendationsQuery,
  GetMacroAssortmentInput,
  GetMacroAssortmentResponse,
  GetMacroAssortmentDocument,
  GetMacroAssortmentQuery,
  AssortmentEntry,
  GetBatchAssortmentSwapRecommendationsDocument,
  GetBatchAssortmentSwapRecommendationsInput,
  GetBatchAssortmentSwapRecommendationsQuery,
  GetBatchAssortmentSwapRecommendationsResponse,
  GetZoneMapConfigInput,
  GetZoneMapConfigResponse,
  GetZoneMapConfigQuery,
  GetZoneMapConfigDocument,
  PromoteDraftZoneInput,
  PromoteDraftZoneResponse,
  PromoteDraftZoneMutation,
  PromoteDraftZoneDocument,
} from 'src/graphql/generated/graphql';
import { fetcher } from '@graphql/utils/fetcher';
import { getTimestampFromDateValue } from '../utils/dateHelper';

export interface MacroPogDetailsAccessor {
  getMacroplanogramEntries(query: {}): Promise<GetMacroplanogramResponse>;
  getAssortmentDiff(variables: GetAssortmentDiffInput): Promise<GetAssortmentDiffResponse>;
  getAllMacroAssortment(variables: GetMacroAssortmentInput): Promise<GetMacroAssortmentResponse>;
  getBatchAllMacroAssortment(variables: GetBatchMacroAssortmentInput): Promise<GetBatchMacroAssortmentItemResponse[]>;
  getAssortmentSwapRecommendations(
    variables: GetAssortmentSwapRecommendationsInput,
  ): Promise<GetAssortmentSwapRecommendationsResponse>;
  getBatchAssortmentSwapRecommendations(
    variables: GetBatchAssortmentSwapRecommendationsInput,
  ): Promise<GetBatchAssortmentSwapRecommendationsResponse>;
  getZoneMapConfig(variables: GetZoneMapConfigInput): Promise<GetZoneMapConfigResponse>;
  promoteDraftZone(variables: PromoteDraftZoneInput): Promise<PromoteDraftZoneResponse>;
}

export interface GetBatchMacroAssortmentInput extends Omit<GetMacroAssortmentInput, 'macroplanogramId'> {
  macroplanogramIds: string[];
}

export interface GetBatchMacroAssortmentItemResponse extends GetMacroAssortmentResponse {
  macroplanogramId: string;
}

export class FreshMacroPogDetailsAccessor implements MacroPogDetailsAccessor {
  private queryClient: QueryClient;

  constructor(queryClient?: QueryClient) {
    this.queryClient = queryClient || new QueryClient();
  }

  async getMacroplanogramEntries(variables: GetMacroplanogramInput): Promise<GetMacroplanogramResponse> {
    const result = await this.queryClient.fetchQuery<GetMacroplanogramQuery>({
      queryKey: ['GetMacroplanogramQuery', variables],
      queryFn: fetcher(GetMacroplanogramDocument, {
        input: {
          ...variables,
          timestamp: getTimestampFromDateValue(variables.timestamp),
        },
      }),
    });

    return result.getMacroplanogram;
  }

  async getMacroAssortment(variables: GetMacroAssortmentInput): Promise<GetMacroAssortmentResponse> {
    const result = await this.queryClient.fetchQuery<GetMacroAssortmentQuery>({
      queryKey: ['GetMacroAssortmentQuery', variables],
      queryFn: fetcher(GetMacroAssortmentDocument, {
        input: {
          ...variables,
          timestamp: getTimestampFromDateValue(variables.timestamp),
        },
      }),
    });

    return result.getMacroAssortment as GetMacroAssortmentResponse;
  }

  async getAllMacroAssortment(variables: GetMacroAssortmentInput): Promise<GetMacroAssortmentResponse> {
    const allAssortment: AssortmentEntry[] = [];
    let paginationToken: string | null | undefined = variables.paginationToken;

    do {
      const response = await this.getMacroAssortment({
        ...variables,
        paginationToken,
      });

      // Add current page's assortment to the accumulated results
      if (response.assortment) {
        allAssortment.push(...response.assortment);
      }

      // Update pagination token for next iteration
      paginationToken = response.paginationToken;
    } while (paginationToken);

    // Return combined results with no pagination token (since we've fetched everything)
    return {
      assortment: allAssortment,
      paginationToken: null,
    };
  }

  async getAssortmentDiff(variables: GetAssortmentDiffInput): Promise<GetAssortmentDiffResponse> {
    const result = await this.queryClient.fetchQuery<GetAssortmentDiffQuery>({
      queryKey: ['GetAssortmentDiffQuery', variables],
      queryFn: fetcher(GetAssortmentDiffDocument, {
        input: {
          ...variables,
          timestamp: getTimestampFromDateValue(variables.timestamp),
        },
      }),
    });

    return result.getAssortmentDiff as GetAssortmentDiffResponse;
  }

  async getAssortmentSwapRecommendations(
    variables: GetAssortmentSwapRecommendationsInput,
  ): Promise<GetAssortmentSwapRecommendationsResponse> {
    const result = await this.queryClient.fetchQuery<GetAssortmentSwapRecommendationsQuery>({
      queryKey: ['GetAssortmentSwapRecommendationsInput', variables],
      queryFn: fetcher(GetAssortmentSwapRecommendationsDocument, {
        input: {
          ...variables,
          endTime: getTimestampFromDateValue(variables.endTime),
          swapTime: getTimestampFromDateValue(variables.swapTime),
        },
      }),
    });

    return result.getAssortmentSwapRecommendations as GetAssortmentSwapRecommendationsResponse;
  }

  async getBatchAssortmentSwapRecommendations(
    variables: GetBatchAssortmentSwapRecommendationsInput,
  ): Promise<GetBatchAssortmentSwapRecommendationsResponse> {
    const result = await this.queryClient.fetchQuery<GetBatchAssortmentSwapRecommendationsQuery>({
      queryKey: ['GetBatchAssortmentSwapRecommendations', variables],
      queryFn: fetcher(GetBatchAssortmentSwapRecommendationsDocument, {
        input: {
          ...variables,
          batchAssortmentSwap: variables.batchAssortmentSwap.map((assortmentSwap) => ({
            ...assortmentSwap,
            endTime: getTimestampFromDateValue(assortmentSwap.endTime),
            swapTime: getTimestampFromDateValue(assortmentSwap.swapTime),
          })),
        },
      }),
    });

    return result.getBatchAssortmentSwapRecommendations as GetBatchAssortmentSwapRecommendationsResponse;
  }

  async getBatchAllMacroAssortment(
    variables: GetBatchMacroAssortmentInput,
  ): Promise<GetBatchMacroAssortmentItemResponse[]> {
    const { macroplanogramIds, ...baseVariables } = variables;
    const promises = macroplanogramIds.map((id) =>
      this.getAllMacroAssortment({
        macroplanogramId: id,
        ...baseVariables,
      }).then(
        (result) =>
          ({
            ...result,
            macroplanogramId: id,
          }) as GetBatchMacroAssortmentItemResponse,
      ),
    );

    return await Promise.all(promises);
  }

  async getZoneMapConfig(variables: GetZoneMapConfigInput): Promise<GetZoneMapConfigResponse> {
    const result = await this.queryClient.fetchQuery<GetZoneMapConfigQuery>({
      queryKey: ['GetZoneMapConfig', variables],
      queryFn: fetcher(GetZoneMapConfigDocument, {
        input: {
          ...variables,
        },
      }),
    });

    return result.getZoneMapConfig;
  }

  async promoteDraftZone(variables: PromoteDraftZoneInput): Promise<PromoteDraftZoneResponse> {
    const result = await this.queryClient.fetchQuery<PromoteDraftZoneMutation>({
      queryKey: ['PromoteDraftZone', variables],
      queryFn: fetcher(PromoteDraftZoneDocument, {
        input: {
          ...variables,
          timestamp: getTimestampFromDateValue(variables.timestamp),
        },
      }),
    });

    return result.promoteDraftZone;
  }
}
